/**
 * ClauseTaggedMarkdown — Renders drafted contract markdown with stable
 * per-clause DOM anchors consumed by Phase-2 annotations.
 *
 * The clause map is computed server-side
 * (pipelines/annotation_hooks.compute_clause_boundaries) and arrives on
 * `message.meta.clause_boundaries` as:
 *
 *   [{ clause_id, header, start_offset, end_offset }, ...]
 *
 * Offsets are character indices into the original draft text. We split
 * the markdown on those offsets, render each clause segment with its
 * own `<ReactMarkdown>` inside a `<div data-clause-id="<id>">`, and
 * render any preamble / gaps as un-anchored markdown. No client-side
 * regex, no parser drift.
 *
 * Fallback: if `boundaries` is empty or unusable, we render the
 * content as plain ReactMarkdown so the chat keeps working.
 */
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';

const isValidBoundary = (b, contentLen) =>
  b &&
  typeof b.clause_id === 'string' &&
  Number.isInteger(b.start_offset) &&
  Number.isInteger(b.end_offset) &&
  b.start_offset >= 0 &&
  b.end_offset > b.start_offset &&
  b.end_offset <= contentLen;

export const ClauseTaggedMarkdown = ({ content, boundaries, components }) => {
  const safeContent = typeof content === 'string' ? content : '';
  const segments = useMemo(() => {
    const list = Array.isArray(boundaries) ? boundaries : [];
    const valid = list
      .filter((b) => isValidBoundary(b, safeContent.length))
      .sort((a, b) => a.start_offset - b.start_offset);

    if (valid.length === 0) return null;

    const out = [];
    let cursor = 0;
    valid.forEach((b) => {
      if (b.start_offset > cursor) {
        // Preamble / gap between clauses — render but don't anchor.
        out.push({
          key: `pre-${b.clause_id}`,
          clauseId: null,
          text: safeContent.slice(cursor, b.start_offset),
        });
      }
      out.push({
        key: `clause-${b.clause_id}`,
        clauseId: b.clause_id,
        header: b.header,
        text: safeContent.slice(b.start_offset, b.end_offset),
      });
      cursor = b.end_offset;
    });
    if (cursor < safeContent.length) {
      out.push({
        key: 'post',
        clauseId: null,
        text: safeContent.slice(cursor),
      });
    }
    return out;
  }, [safeContent, boundaries]);

  if (!segments) {
    return <ReactMarkdown components={components}>{safeContent}</ReactMarkdown>;
  }

  return (
    <>
      {segments.map((seg) =>
        seg.clauseId ? (
          <div
            key={seg.key}
            data-clause-id={seg.clauseId}
            data-clause-header={seg.header || ''}
            data-testid={`clause-anchor-${seg.clauseId}`}
            className="annotation-anchor"
          >
            <ReactMarkdown components={components}>{seg.text}</ReactMarkdown>
          </div>
        ) : (
          seg.text.trim() && (
            <ReactMarkdown key={seg.key} components={components}>
              {seg.text}
            </ReactMarkdown>
          )
        )
      )}
    </>
  );
};

export default ClauseTaggedMarkdown;
